import { PublicRoutes } from "@/constants/routes";
import { CalendarX2, Gift, Phone, Trash2 } from "lucide-react";
import { ACCOUNT_TERMS } from "./company";
import type { LegalDocument } from "./types";

const { trialDays, readOnlyDays, retentionDays } = ACCOUNT_TERMS;

export const CANCELLATION: LegalDocument = {
  key: "cancelamento",
  path: PublicRoutes.POLITICA_CANCELAMENTO,
  eyebrow: "Contratação",
  title: "Política de Cancelamento",
  seoTitle: "Política de Cancelamento: sem multa nem fidelidade",
  description: `Como cancelar a Fidexa: teste grátis de ${trialDays} dias, sem multa e sem tempo mínimo, cobrança do mês, acesso aos dados depois do cancelamento e número do WhatsApp.`,
  intro:
    "Entrar na Fidexa tem de ser tão simples quanto sair. Aqui está tudo o que acontece quando você cancela: o que é cobrado, por quanto tempo os dados ficam disponíveis e o que acontece com o número de WhatsApp.",
  version: "1.0",
  updatedAt: "2026-10-12",

  highlights: [
    {
      icon: Gift,
      title: `${trialDays} dias de teste grátis`,
      text: "Teste a Fidexa sem pagar nada. Não pedimos cartão nem cobramos nada no teste.",
    },
    {
      icon: CalendarX2,
      title: "Sem multa, sem fidelidade",
      text: "Não há tempo mínimo de contrato nem multa por cancelamento. Cancele quando quiser.",
    },
    {
      icon: Trash2,
      title: `${readOnlyDays} dias para levar seus dados`,
      text: `Depois do fim do acesso, a plataforma fica disponível para leitura por ${readOnlyDays} dias e você pode pedir a cópia por e-mail.`,
    },
    {
      icon: Phone,
      title: "O número da Fidexa fica com a Fidexa",
      text: "O número de WhatsApp fornecido pela Fidexa não é transferido nem portado para outro CNPJ.",
    },
  ],

  sections: [
    {
      id: "teste-gratis",
      title: "Teste grátis",
      summary: `Você usa a Fidexa por até ${trialDays} dias sem pagar nada. Se não contratar, nada é cobrado.`,
      blocks: [
        {
          type: "paragraph",
          text: `A loja pode testar a plataforma por até **${trialDays} dias corridos**, sem custo e sem precisar informar dados de pagamento. O teste não vira contrato sozinho: ao final, a loja decide se contrata.`,
        },
        {
          type: "paragraph",
          text: "Se a loja não contratar, o acesso é encerrado ao fim do teste e os dados seguem as mesmas regras de leitura, cópia e eliminação desta política.",
        },
      ],
    },
    {
      id: "sem-fidelidade",
      title: "Sem tempo mínimo e sem multa",
      summary:
        "Não existe período mínimo de permanência nem multa. Você pode pedir o cancelamento a qualquer momento.",
      blocks: [
        {
          type: "paragraph",
          text: "A contratação da Fidexa é mensal e não tem prazo mínimo de permanência. A loja pode pedir o cancelamento a qualquer momento, **sem multa e sem aviso prévio**.",
        },
        {
          type: "subheading",
          text: "Como pedir",
        },
        {
          type: "list",
          ordered: true,
          items: [
            "Peça o cancelamento por escrito, pelo e-mail ou pelo WhatsApp do suporte, a partir do contato do responsável pela conta.",
            "Confirmamos o pedido por escrito, com a data do fim do acesso e os prazos desta política.",
          ],
        },
        {
          type: "paragraph",
          text: "Pedimos que o cancelamento venha do responsável pela conta para proteger a loja: assim ninguém cancela o serviço, nem recebe os dados dela, em nome de outra pessoa.",
        },
      ],
    },
    {
      id: "cobranca",
      title: "O que é cobrado",
      summary:
        "O mês em curso é cobrado inteiro, sem cálculo proporcional, e o acesso completo continua até o fim dele. Nenhuma mensalidade nova é gerada depois do cancelamento.",
      blocks: [
        {
          type: "list",
          items: [
            "**Mês em curso:** a mensalidade do mês em que o cancelamento é pedido é cobrada por inteiro, sem desconto proporcional aos dias de uso. Em troca, a loja continua com acesso completo até o último dia desse mês.",
            "**Meses seguintes:** nenhuma mensalidade é gerada depois do cancelamento.",
            "**Consumo variável:** custos de uso já realizados até o fim do acesso, como o volume de mensagens de WhatsApp previsto na proposta comercial, continuam devidos.",
            "**Valores em aberto:** o cancelamento não perdoa faturas vencidas antes dele, que seguem as regras de atraso do contrato.",
          ],
        },
      ],
    },
    {
      id: "depois-do-cancelamento",
      title: "Seus dados depois do cancelamento",
      summary: `Por ${readOnlyDays} dias depois do fim do acesso, você consulta os dados e pode pedir a cópia por e-mail. Depois, os dados ficam guardados e bloqueados até completar ${retentionDays} dias e são eliminados.`,
      blocks: [
        {
          type: "table",
          caption: "Linha do tempo depois do cancelamento",
          columns: [
            { key: "quando", header: "Quando", primary: true },
            { key: "oque", header: "O que acontece" },
          ],
          rows: [
            {
              quando: "Até o fim do mês pago",
              oque: "Acesso completo à plataforma, como antes do pedido.",
            },
            {
              quando: `Do fim do acesso até ${readOnlyDays} dias depois`,
              oque: "A plataforma fica **só para leitura**: dá para consultar clientes, parcelas e conversas, mas não para enviar mensagens. A loja pode pedir a **cópia dos dados**, enviada por e-mail.",
            },
            {
              quando: `Do ${readOnlyDays + 1}º ao ${retentionDays}º dia`,
              oque: "Os dados ficam **guardados e bloqueados**: sem acesso da loja, sem uso para nenhuma finalidade e sem entrega de novas cópias.",
            },
            {
              quando: `Depois de ${retentionDays} dias`,
              oque: "Os dados são **eliminados** de forma definitiva, inclusive das cópias de segurança, no ciclo seguinte de rotação delas.",
            },
          ],
        },
        {
          type: "subheading",
          text: "A cópia dos dados",
        },
        {
          type: "paragraph",
          text: `Durante os ${readOnlyDays} dias de leitura, a loja pode pedir uma cópia dos dados no formato padrão da plataforma. Ela é enviada ao e-mail do responsável pela conta. Baixar e guardar a cópia é responsabilidade da loja: **passado esse prazo, a Fidexa não entrega mais cópias**, mesmo que os dados ainda estejam na guarda bloqueada.`,
        },
        {
          type: "subheading",
          text: `Por que guardamos por ${retentionDays} dias`,
        },
        {
          type: "paragraph",
          text: "A guarda serve para a segurança da loja e da Fidexa: comprovar o que foi feito na plataforma em caso de reclamação, disputa judicial ou pedido de autoridade (LGPD, art. 7º, VI) e investigar fraude ou incidente de segurança. Por isso os dados ficam **bloqueados** (LGPD, art. 5º, XIII): ninguém da loja acessa, e a Fidexa só os consulta para essas finalidades.",
        },
        {
          type: "paragraph",
          text: `Notas fiscais e registros de acesso seguem os prazos legais próprios, descritos em [Retenção de dados](${PublicRoutes.RETENCAO_DADOS}).`,
        },
      ],
    },
    {
      id: "numero-whatsapp",
      title: "O número de WhatsApp",
      summary:
        "Se o número de WhatsApp foi fornecido pela Fidexa, ele é da Fidexa e continua com ela. Não é transferido, cedido nem portado para nenhum outro CNPJ.",
      blocks: [
        {
          type: "paragraph",
          text: "Quando a Fidexa fornece o número de WhatsApp usado pela loja, a linha telefônica e a conta na WhatsApp Business Platform são **de titularidade e responsabilidade da Fidexa**. O número é uma ferramenta do serviço, disponibilizada à loja enquanto o contrato estiver em vigor.",
        },
        {
          type: "list",
          items: [
            "O número **não é transferido, cedido nem portado** para outro CNPJ, inclusive o da própria loja, em nenhum momento: nem durante o contrato, nem depois do cancelamento.",
            "Como a loja não é titular da linha, não há portabilidade numérica a pedir à operadora.",
            "Depois do fim do acesso, as mensagens recebidas nesse número deixam de ser encaminhadas à loja.",
          ],
        },
        {
          type: "callout",
          tone: "info",
          title: "Avise seus clientes antes de sair",
          text: "Antes do fim do acesso, informe aos seus clientes os canais próprios da loja para pagamento e atendimento. Assim ninguém fica sem saber com quem falar.",
        },
        {
          type: "paragraph",
          text: "Se a loja usa um número de WhatsApp próprio, registrado em nome dela, o número continua sendo da loja e é desconectado da Fidexa no cancelamento.",
        },
      ],
    },
    {
      id: "contrato",
      title: "Esta política e o seu contrato",
      summary:
        "Estas regras valem para todas as contratações. Se o seu contrato tiver uma condição mais favorável à loja, ela é mantida.",
      blocks: [
        {
          type: "paragraph",
          text: "Esta política complementa o contrato de licenciamento da Fidexa. Se o contrato da loja trouxer uma condição de cancelamento mais favorável a ela, essa condição prevalece.",
        },
      ],
    },
  ],

  changelog: [
    {
      version: "1.0",
      date: "2026-10-12",
      changes: ["Primeira versão publicada."],
    },
  ],
};
